import React from "react";
import Moment from "react-moment";
import SlideOutSidebar from "../../ui-components/slide-out-sidebar/slideOutSidebar";
import Amount from "../../ui-components/molecules/amount/amount";
import Button, { ButtonSizes, ButtonThemes } from "../../ui-components/button/button";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import { PublicPaymentLinkSummary } from "../../app/data/payment/PaymentSession";
import { formatProbillDisplay } from "../shipments/labels-receipts/types";

export interface PublicPaymentLinkDetailProps {
  show: boolean;
  isLoading: boolean;
  link: PublicPaymentLinkSummary | null;
  error: string | null;
  onClose: () => void;
  onViewPayment: (paymentId: string) => void;
}

const when = (value?: string) =>
  value ? <Moment format="MM/DD/YYYY h:mm A">{value}</Moment> : "—";

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="xgs-public-payment-links__detail-field">
    <div className="xgs-public-payment-links__detail-label">{label}</div>
    <div className="xgs-public-payment-links__detail-value">{children}</div>
  </div>
);

const Step: React.FC<{ label: string; whenLabel: React.ReactNode; detail?: React.ReactNode }> = ({
  label,
  whenLabel,
  detail,
}) => (
  <li className="xgs-public-payment-links__step">
    <div className="xgs-public-payment-links__step-mark" />
    <div>
      <div className="xgs-public-payment-links__step-label">{label}</div>
      <div className="xgs-public-payment-links__step-when">{whenLabel}</div>
      {detail && <div className="xgs-public-payment-links__step-detail">{detail}</div>}
    </div>
  </li>
);

const PublicPaymentLinkDetail: React.FC<PublicPaymentLinkDetailProps> = ({
  show,
  isLoading,
  link,
  error,
  onClose,
  onViewPayment,
}) => (
  <SlideOutSidebar header="Link usage" show={show} onClose={onClose} spinner={isLoading} size="small-500">
    <div className="xgs-public-payment-links__detail">
      {error && <XGSErrorMessage>{error}</XGSErrorMessage>}
      {link && (
        <>
          <Field label="Status">
            {link.status ? link.status.charAt(0) + link.status.slice(1).toLowerCase() : "—"}
          </Field>
          <Field label="Email">{link.email || "—"}</Field>
          <Field label="Account">
            {link.accountName || link.accountNumber
              ? [link.accountName, link.accountNumber].filter(Boolean).join(" · ")
              : "—"}
          </Field>
          <Field label="XGS probill numbers">
            {(link.probillNumbers || []).map((number) => formatProbillDisplay(number)).join(", ") || "—"}
          </Field>
          <h3>Usage</h3>
          <ol className="xgs-public-payment-links__timeline">
            <Step
              label="Created"
              whenLabel={when(link.dateCreated)}
              detail={link.createdBy}
            />
            <Step
              label="Opened"
              whenLabel={link.openCount ? when(link.lastOpenedAt) : "Not opened yet"}
              detail={
                link.openCount ? (
                  <>
                    {`${link.openCount} open${link.openCount === 1 ? "" : "s"}`}
                    {link.firstOpenedAt && (
                      <>
                        {" · first "}
                        <Moment format="MM/DD/YYYY h:mm A">{link.firstOpenedAt}</Moment>
                      </>
                    )}
                  </>
                ) : undefined
              }
            />
            <Step
              label="Checkout started"
              whenLabel={link.checkoutStartedAt ? when(link.checkoutStartedAt) : "Not started"}
            />
            {link.status === "REVOKED" ? (
              <Step label="Revoked" whenLabel={link.revokedBy || "Revoked"} />
            ) : (
              <Step
                label="Paid"
                whenLabel={link.paidAt ? when(link.paidAt) : "Not paid"}
              />
            )}
          </ol>
          <Field label="Expires">{when(link.expiresAt)}</Field>
          {(link.paymentId || link.paymentAmountCents != null) && (
            <>
              <h3>Payment</h3>
              <Field label="Payment ID">{link.paymentId || "—"}</Field>
              <Field label="Method">{link.paymentMethod || "—"}</Field>
              <Field label="Amount">
                {link.paymentAmountCents != null ? (
                  <Amount amount={link.paymentAmountCents / 100} />
                ) : (
                  "—"
                )}
              </Field>
              {link.paymentId && (
                <Button
                  theme={ButtonThemes.blue}
                  size={ButtonSizes.autoMedium}
                  type="button"
                  className="xgs-public-payment-links__view-payment"
                  onClick={() => onViewPayment(link.paymentId as string)}
                >
                  View payment details
                </Button>
              )}
            </>
          )}
        </>
      )}
    </div>
  </SlideOutSidebar>
);

export default PublicPaymentLinkDetail;
